import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { baseAPI } from '../../config';
import MainLayout from '../../layouts/MainLayout';
import DashboardCard09 from '../../components/DashboardCard09';
import AnalyticsCard06 from '../../components/AnalyticsCard06';
import NewDebtChart from '../../components/NewDebtChart';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import {
  BanknotesIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CurrencyEuroIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import UserSelect from '../../components/userSelectList';
import userStore from '../../store/userStore';
import Tooltip from '../../components/Tooltip';

const DataDashboard = () => {
  const [totals, setTotals] = useState({
    totalReclamaciones: 0,
    totalDeuda: 0,
    totalRecuperado: 0,
    totalPendiente: 0,
    importesRecuperadosPorDia: [],
    estadosAgrupados: [],
    porcentajeRecuperado: 0,
    reclamacionesActivas: 0,
    reclamacionesCerradas: 0,
    importePromedioDeuda: 0,
    initialTotalDebt: 0,
    monthlyDebt: [],
  });
  const [filterUser, setFilterUser] = useState('');
  const { user } = userStore();

  // Nuevos estados para las fechas (strings en formato YYYY-MM-DD)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Estado para mostrar/ocultar filtros
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const params = { filterUser };
      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) params.endDate = new Date(endDate).toISOString();

      const { data } = await axios.get(`${baseAPI}/expedients/dashboard`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotals(data);
    } catch (error) {
      console.error(error);
    }
  }, [filterUser, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = {
    labels: totals.importesRecuperadosPorDia.map(item => item.fecha),
    datasets: [
      {
        label: 'Importes cobrados',
        data: totals.importesRecuperadosPorDia.map(item => item.importe),
        barPercentage: 0.66,
        categoryPercentage: 0.66,
      },
    ],
  };

  const {
    totalReclamaciones,
    totalDeuda,
    totalRecuperado,
    totalPendiente,
    porcentajeRecuperado,
    reclamacionesActivas,
    reclamacionesCerradas,
    importePromedioDeuda,
    initialTotalDebt,
  } = totals;

  return (
    <MainLayout>
      <div className='bg-slate-50 min-h-full'>
        {/* Header Section */}
        <div className='bg-white shadow-sm rounded-lg border border-slate-200 p-6 mb-8'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between'>
          {/* Title and Description */}
          <div className='mb-6 lg:mb-0'>
            <h1 className='text-2xl font-semibold text-slate-900 flex items-center gap-3'>
              Resumen de la cartera
            </h1>
            <p className='text-sm text-slate-500 mt-1'>
              Cómo va el cobro de tu cartera, al día.
            </p>
          </div>

          {/* Toggle Button for Filters */}
          <div className='w-full lg:w-auto'>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center justify-center px-4 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full lg:w-auto'
            >
              {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              <span className='ml-2'>
                {showFilters ? (
                  <ChevronUpIcon className='h-5 w-5 transition-transform duration-200' />
                ) : (
                  <ChevronDownIcon className='h-5 w-5 transition-transform duration-200' />
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Filters Section - Conditionally Rendered */}
        {showFilters && (
          <div className='mt-6 pt-6 border-t border-slate-200'>
            <div className='mb-4'>
              <h3 className='text-lg font-medium text-slate-900'>Filtros de búsqueda</h3>
              <p className='mt-1 text-sm text-slate-500'>Ajusta los filtros para visualizar datos específicos</p>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6'>
              {/* Usuario Select - Ocupa 4 columnas en lg */}
              {user.role === 'admin' && (
                <div className='lg:col-span-4'>
                  <div className='bg-white p-4 rounded-lg border border-slate-200 shadow-sm'>
                    <label className='block text-sm font-medium text-slate-700 mb-2'>Filtrar por Usuario</label>
                    <UserSelect selectedUser={filterUser} onUserChange={user => setFilterUser(user)} />
                    {filterUser && (
                      <button
                        onClick={() => setFilterUser('')}
                        className='mt-2 inline-flex items-center text-sm text-red-500 hover:text-red-700'
                      >
                        <svg className='w-4 h-4 mr-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                        </svg>
                        Limpiar selección
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Grupo de Fechas - Ocupa 8 columnas en lg */}
              <div className='lg:col-span-8'>
                <div className='bg-white p-4 rounded-lg border border-slate-200 shadow-sm'>
                  <div className='flex items-center justify-between mb-4'>
                    <label className='block text-sm font-medium text-slate-700'>Rango de Fechas de Creación</label>
                    {(startDate || endDate) && (
                      <button
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                        }}
                        className='inline-flex items-center text-sm text-red-500 hover:text-red-700'
                      >
                        <svg className='w-4 h-4 mr-1' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                        </svg>
                        Limpiar fechas
                      </button>
                    )}
                  </div>

                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-slate-600 mb-1' htmlFor='startDate'>
                        Fecha Inicio
                      </label>
                      <input
                        type='date'
                        id='startDate'
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        max={endDate || undefined}
                        className='w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150'
                      />
                    </div>

                    <div>
                      <label className='block text-sm font-medium text-slate-600 mb-1' htmlFor='endDate'>
                        Fecha Fin
                      </label>
                      <input
                        type='date'
                        id='endDate'
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        min={startDate || undefined}
                        className='w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de filtros activos */}
            {(filterUser || startDate || endDate) && (
              <div className='mt-4 flex flex-wrap gap-2'>
                {filterUser && (
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
                    Usuario: {filterUser}
                  </span>
                )}
                {startDate && (
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
                    Desde: {new Date(startDate).toLocaleDateString()}
                  </span>
                )}
                {endDate && (
                  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
                    Hasta: {new Date(endDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className='space-y-8'>
        {/* Primera fila de métricas principales */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
          <DashboardCard
            title='Deuda total'
            value={totalDeuda}
            formatCurrency
            colorClass='bg-blue-600'
            icon={<CurrencyEuroIcon className='h-6 w-6 text-slate-400' />}
            initialTotalDebt={initialTotalDebt}
          />
          <DashboardCard
            title='Total cobrado'
            value={totalRecuperado}
            formatCurrency
            colorClass='bg-blue-600'
            icon={<BanknotesIcon className='h-6 w-6 text-slate-400' />}
          />
          <DashboardCard
            title='Pendiente de cobro'
            value={totalPendiente}
            formatCurrency
            colorClass='bg-blue-600'
            icon={<ClockIcon className='h-6 w-6 text-slate-400' />}
          />
          <DashboardCard
            title='Tasa de cobro'
            value={porcentajeRecuperado}
            suffix='%'
            colorClass='bg-blue-600'
            icon={<ArrowTrendingUpIcon className='h-6 w-6 text-slate-400' />}
          />
        </div>

        {/* Segunda fila de métricas adicionales */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
          <DashboardCard
            title='Expedientes'
            value={totalReclamaciones}
            colorClass='bg-blue-600'
            icon={<ChartBarIcon className='h-6 w-6 text-slate-400' />}
          />
          <DashboardCard
            title='Cerrados'
            value={reclamacionesCerradas}
            colorClass='bg-blue-600'
            icon={<CheckCircleIcon className='h-6 w-6 text-slate-400' />}
          />

          <DashboardCard
            title='En gestión'
            value={reclamacionesActivas}
            colorClass='bg-blue-600'
            icon={<ArrowPathIcon className='h-6 w-6 text-slate-400' />}
          />

          <DashboardCard
            title='Importe medio'
            value={importePromedioDeuda}
            formatCurrency
            colorClass='bg-blue-600'
            icon={<CalculatorIcon className='h-6 w-6 text-slate-400' />}
          />
        </div>

        <DashboardCard09 chartData={chartData} />
        {/* Nuevo gráfico de deuda por mes - Solo visible para admin */}
        {user?.role === 'admin' && <NewDebtChart monthlyDebt={totals.monthlyDebt} />}
        <AnalyticsCard06 estadosAgrupados={totals.estadosAgrupados} />
      </div>
      </div>
    </MainLayout>
  );
};

const DashboardCard = ({
  title,
  value,
  formatCurrency,
  colorClass = 'bg-slate-500',
  icon,
  suffix,
  initialTotalDebt,
}) => (
  <div className='bg-white shadow-sm rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow duration-150'>
    <header className='flex items-center justify-between mb-4'>
      <h2 className='text-lg font-semibold text-slate-800'>{title}</h2>
      <span aria-hidden='true'>{icon}</span>
    </header>
    <div className='flex items-center'>
      <div className={`w-2 h-12 ${colorClass} rounded-full mr-4`} aria-hidden='true'></div>
      <div className='text-2xl font-bold text-slate-800'>
        {formatCurrency ? (
          <NumericFormat
            value={value}
            displayType={'text'}
            thousandSeparator={'.'}
            decimalSeparator={','}
            suffix={' €'}
          />
        ) : (
          <>
            {value}
            {suffix && <span className='ml-1'>{suffix}</span>}
          </>
        )}
      </div>
    </div>
    {initialTotalDebt && (
      <div className='ml-6 flex items-center'>
        <span className='text-sm text-slate-500'>
          Deuda Inicial:{' '}
          <NumericFormat
            value={initialTotalDebt}
            displayType={'text'}
            thousandSeparator={'.'}
            decimalSeparator={','}
            suffix={' €'}
          />
        </span>
        <Tooltip className='ml-2' size='xl'>
          <span className='text-sm text-slate-500'>
            La deuda inicial es la suma de todas las deudas de los expedientes que se han creado en el rango de fechas
            seleccionado.
            <br />A esta se le resta los expedientes cancelados por el cliente o incompletos (sin datos de contacto).
          </span>
        </Tooltip>
      </div>
    )}
  </div>
);

export default DataDashboard;
